// ============================================================
// All editable site content lives in this file.
// Update the values below and the whole site updates with them.
// ============================================================

import handbookCatalog from "./handbooks/catalog.json";

const CAREER_START = new Date(2017, 0, 1); // Jan 2017
export const yearsOfExperience = Math.round(
  (Date.now() - CAREER_START.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
);

export const profile = {
  name: "Bipin Singh",
  role: "Senior Full-Stack Engineer",
  headline: "MERN · AWS Serverless · AI-Augmented Delivery",
  tagline:
    "I embed with teams and customers, turn messy business problems into production systems, and ship AI into real workflows — end to end.",
  // Shown as a badge in the hero and contact sections. Set to "" to hide it.
  availability: "Open to Forward Deployed & Solutions Engineering roles · onsite / international",
  email: "bpinkrsingh@gmail.com",
  phone: "+91 7774010199",
  location: "Pune, India",
  relocation: "Open to relocation — onsite & international",
  // Paste your profile URLs here. Icons only show up for links that are filled in.
  social: {
    github: "https://github.com/bpinsingh",
    linkedin: "https://www.linkedin.com/in/bpinsingh",
    twitter: "https://x.com/bpinsingh",
    hashnode: "https://hashnode.com/@bpinsingh",
  },
  handle: "bpinsingh",
};

export const navLinksdata = [
  { _id: 1001, title: "Home", link: "home" },
  { _id: 1002, title: "About", link: "about" },
  { _id: 1003, title: "Work", link: "projects" },
  { _id: 1004, title: "Experience", link: "experience" },
  { _id: 1005, title: "Skills", link: "skills" },
  { _id: 1007, title: "Handbooks", link: "handbooks" },
  { _id: 1006, title: "Contact", link: "contact" },
];

// Headline numbers shown in the hero.
export const impactStats = [
  { value: `${yearsOfExperience}`, label: "Years shipping production software" },
  { value: "500+", label: "Daily users on my ops console" },
  { value: "~55%", label: "Faster ticket triage with LLMs" },
  { value: "~30%", label: "AWS infra cost cut" },
];

export const aboutText = [
  `I'm a Senior Full-Stack Engineer with ${yearsOfExperience} years delivering production-grade web applications for Indian enterprises and global product companies — across BFSI, telecom, healthtech and enterprise SaaS.`,
  "My sweet spot is the space between the customer and the code: sitting with stakeholders to understand the real problem, designing the system, shipping it on AWS, and owning it after launch. Lately that means putting LLMs into real workflows — function calling, RAG and guardrails — not just demos.",
  "I'm looking for Forward Deployed, Solutions or Tech Lead roles where I can work directly with customers on high-impact AI and cloud platforms — and I'm open to onsite and international opportunities.",
];

// The forward-deployed skill set. `icon` must be a key from src/components/layouts/techIcons.js
export const servicesData = [
  {
    icon: "handshake",
    title: "Embedded With Customers",
    des: "From client requirement workshops and demos at Angular Minds to a Fortune-500 Deloitte engagement — I translate stakeholder needs into shipped software.",
  },
  {
    icon: "brain",
    title: "AI Into Production",
    des: "LLM function calling, RAG, prompt templates and guardrails in shipped tools — like ticket triage that went from ~12 to ~5 minutes per ticket.",
  },
  {
    icon: "cloud",
    title: "Cloud-Native Delivery",
    des: "AWS Serverless architectures with Lambda, EventBridge, SQS and DynamoDB — modernising legacy mainframe batch jobs and cutting infra cost by ~30%.",
  },
  {
    icon: "users",
    title: "Lead & Multiply",
    des: "Onboarded 18 engineers to AI pair-programming, set testing standards across 4 squads, and mentored engineers — two promoted within the cycle.",
  },
];

// `github` / `live` are optional — the matching icon is hidden when left empty.
// `featured: true` shows the project as a large card at the top.
// `logo` is a key exported from src/assets/index.js; without it the cover is drawn from `tags`.
// `status` shows as a small badge; `metric` shows as a highlighted result line.
export const projectsData = [
  {
    title: "LLM Ticket Triage & Document Q&A",
    context: "TCS",
    metric: "~55% faster triage (12 → 5 min/ticket)",
    des: "Embedded the OpenAI API into internal tooling for ticket triage and document Q&A — designed prompt templates, function-calling schemas and guardrails.",
    tags: ["OpenAI", "Node.js", "TypeScript"],
  },
  {
    title: "Mainframe → AWS Serverless Modernisation",
    context: "TCS",
    metric: "500+ operations users daily",
    des: "Replaced batch COBOL jobs with Lambda + EventBridge pipelines and a React/Node.js operations console used by internal operations teams every day.",
    tags: ["AWS", "React", "Node.js"],
  },
  {
    title: "API Latency Optimisation",
    context: "TCS",
    metric: "p95 latency ~800ms → ~480ms",
    des: "Architected customer-facing modules with React, TypeScript, NestJS and PostgreSQL, then cut p95 latency by ~40% with query tuning, caching and strategic denormalisation.",
    tags: ["NestJS", "PostgreSQL", "React"],
  },
  {
    title: "Event-Driven AWS Workflows",
    context: "Globant · Deloitte engagement",
    metric: "~30% infra cost reduction",
    des: "Replaced synchronous integrations for a Fortune-500 client with event-driven workflows on Lambda, SQS, EventBridge and DynamoDB — more resilient and cheaper to run.",
    tags: ["AWS", "DynamoDB", "NestJS"],
  },
  {
    title: "Billing Platform Data Overhaul",
    context: "Hansen Technologies",
    metric: "Nightly batch ~6h → ~1.5h",
    des: "Redesigned the PostgreSQL schema and index strategy for a high-write reporting workload, and built a secure Salesforce integration with OAuth and bidirectional sync.",
    tags: ["PostgreSQL", "Node.js", "Salesforce"],
  },
  {
    title: "PillPresso — Connected Health",
    context: "Angular Minds",
    metric: "Mobile app + admin portal + IoT telemetry",
    des: "A medication-adherence product: Ionic mobile app, Angular admin portal, Node.js backend and MQTT device telemetry, with OTA configuration and real-time dashboards.",
    tags: ["Ionic", "Angular", "Node.js"],
  },
];

export const experienceData = [
  {
    role: "Senior Full-Stack Engineer",
    company: "Tata Consultancy Services (TCS)",
    period: "Aug 2024 — Present",
    location: "Pune, India",
    points: [
      "Led modernisation of legacy mainframe workflows into a cloud-native AWS Serverless platform — Lambda + EventBridge pipelines and a React/Node.js ops console used by 500+ users daily.",
      "Architected customer-facing modules with React, TypeScript, NestJS and PostgreSQL; cut API p95 latency by ~40% (~800ms → ~480ms).",
      "Embedded LLMs (OpenAI API) into internal tooling for ticket triage and document Q&A with prompt templates, function calling and guardrails — ~55% faster triage.",
      "Drove team-wide adoption of Copilot and Cursor, onboarding 18 engineers and creating a shared prompt library and code-review playbook.",
      "Mentored 4 mid/junior engineers on React, system design and testing — two were promoted within the cycle.",
    ],
  },
  {
    role: "Senior Full-Stack Engineer",
    company: "Globant · Deloitte engagement",
    period: "Jun 2021 — Jul 2024",
    location: "Pune, India",
    points: [
      "Built scalable APIs and admin dashboards for a Fortune-500 Deloitte client with NestJS, TypeScript, PostgreSQL and React, owning modules end-to-end from discovery to post-launch support.",
      "Designed event-driven workflows on AWS (Lambda, SQS, EventBridge, DynamoDB) replacing synchronous integrations — more resilient, ~30% lower infra cost.",
      "Established frontend testing standards (Jest + RTL, AAA pattern) adopted across 4 squads, reducing regression escapes.",
    ],
  },
  {
    role: "Full-Stack Engineer",
    company: "Hansen Technologies",
    period: "Aug 2019 — Jun 2021",
    location: "Pune, India",
    points: [
      "Delivered Salesforce integration with a Node.js/Angular billing platform for telecom and utility clients — secure OAuth flows, Apex callouts and bidirectional sync.",
      "Redesigned PostgreSQL schema and indexing for a high-write reporting workload, cutting nightly batch time from ~6h to ~1.5h.",
      "Owned the production support rotation and reduced time-to-detection with custom CloudWatch dashboards and alarm tuning.",
    ],
  },
  {
    role: "Full-Stack Engineer",
    company: "Angular Minds",
    period: "Jan 2017 — Aug 2019",
    location: "Pune, India",
    points: [
      "Built PillPresso, a connected medication-adherence product: Ionic app, Angular admin portal, Node.js backend and MQTT device telemetry.",
      "Implemented OTA configuration, real-time device dashboards and patient adherence reports with GDPR/HIPAA-style data handling.",
      "Worked directly with clients on requirements and demos across multiple greenfield MERN engagements.",
    ],
  },
];

export const educationData = [
  { degree: "M.Sc. Computer Science", school: "Savitribai Phule Pune University", period: "2015 — 2017" },
  { degree: "B.Sc. Computer Science", school: "Savitribai Phule Pune University", period: "2012 — 2015" },
];

// Names must match keys in src/components/layouts/techIcons.js to get an icon.
export const skillGroups = [
  { title: "AI / LLM", skills: ["OpenAI API", "LLM Integration", "Function Calling", "RAG", "Embeddings", "Prompt Engineering", "Copilot & Cursor"] },
  { title: "Cloud / DevOps", skills: ["AWS Lambda", "API Gateway", "DynamoDB", "S3", "SQS / SNS", "EventBridge", "Cognito", "CDK", "Docker", "GitHub Actions"] },
  { title: "Backend", skills: ["Node.js", "NestJS", "Express", "REST", "GraphQL", "WebSockets", "Microservices", "Event-Driven Design"] },
  { title: "Frontend", skills: ["React", "Next.js", "TypeScript", "Redux / Zustand", "Tailwind CSS", "Angular", "Ionic"] },
  { title: "Databases", skills: ["PostgreSQL", "MongoDB", "DynamoDB", "Redis"] },
  { title: "Practices", skills: ["System Design", "TDD", "Jest & RTL", "Code Reviews", "Agile / Scrum", "Mentoring"] },
];

// "Where I've delivered" strip under the hero.
export const companiesData = [
  { name: "TCS" },
  { name: "Globant" },
  { name: "Deloitte", note: "client" },
  { name: "Hansen" },
  { name: "Angular Minds" },
];

// Scrolling tech strip under the hero.
export const marqueeTech = [
  "OpenAI API", "AWS Lambda", "React", "Node.js", "TypeScript", "NestJS", "PostgreSQL",
  "DynamoDB", "Next.js", "EventBridge", "MongoDB", "Docker", "GraphQL", "RAG", "Redis", "GitHub Actions",
];

// One-liners that rotate in the speech bubble over the hero avatar (click the photo for the next one).
export const heroQuips = [
  "Hi! I ship AI to prod 🚀",
  "Works on my machine… and on AWS ☁️",
  "Powered by chai & TypeScript ☕",
  "Prompting LLMs so you don't have to 🤖",
  "git commit -m \"final_final_v2\" 😅",
  "Fly me to your customers ✈️",
];

// Handbooks shown in the Library section, generated by the build scripts — don't edit by hand:
//   npm run handbooks   → Markdown handbooks in content/handbooks/<slug>/
//   npm run rag:sync    → RAG Handbook, pulled from rag.bipinsingh.in
// Each handbook's card (title, category, colours, chapter count…) comes from its handbook.json.
export const handbooks = handbookCatalog;
